import { useEffect, useRef, useState } from "react";
import { Archive, Upload, Trash2, FileText, Shield } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";

const emptyEntry = (type) => ({
  type,
  seller: "",
  city: "",
  amount_paid: "",
  quantity: "",
  unit: type === "land" ? "acres" : "g",
  purity: type === "gold" ? "22K" : type === "silver" ? "999" : "",
  rate_per_unit: "",
  buyback_terms: "",
  notes: "",
  purchased_on: new Date().toISOString().slice(0, 10),
  invoice_url: "",
  invoice_mime: "",
});

const MAX_INVOICE_BYTES = 1.5 * 1024 * 1024; // 1.5 MB base64 cap

export default function PurchaseVault({ type }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(() => emptyEntry(type));
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/purchase/vault");
      setItems((data.items || []).filter((it) => it.type === type));
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setForm(emptyEntry(type));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_INVOICE_BYTES) {
      toast.error("File too large — keep under 1.5 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((s) => ({ ...s, invoice_url: reader.result, invoice_mime: f.type }));
      toast.success("Invoice attached");
    };
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (saving) return;
    if (!form.seller.trim() && !form.city.trim() && !form.amount_paid) {
      toast.error("Add at least seller, city, or amount paid");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Convert empty strings to null for optional numeric fields
      ["amount_paid", "quantity", "rate_per_unit"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null) payload[k] = null;
        else payload[k] = Number(payload[k]);
      });
      await api.post("/purchase/vault", payload);
      toast.success("Saved to vault");
      setForm(emptyEntry(type));
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/purchase/vault/${id}`);
      setItems((s) => s.filter((it) => it.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div
      className="bg-white border border-line rounded-sm p-5"
      data-testid={`purchase-vault-${type}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Archive className="w-4 h-4 text-amber_brand" />
        <span className="font-serif-display text-lg text-stone_brand-dark font-medium">
          Vault — store bill & buyback terms
        </span>
      </div>
      <p className="text-xs text-stone_brand mb-4">
        Attach the invoice photo and the exact buyback terms the shop quoted. Retrievable anytime — the paperwork is what makes resale possible at full value.
      </p>

      {/* Form */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <input
          value={form.seller}
          onChange={(e) => setForm({ ...form, seller: e.target.value })}
          placeholder="Seller / shop name"
          className="h-10 px-3 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss"
          data-testid="vault-seller"
        />
        <input
          value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
          placeholder="City"
          className="h-10 px-3 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss"
          data-testid="vault-city"
        />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
        <input
          type="number"
          value={form.amount_paid}
          onChange={(e) => setForm({ ...form, amount_paid: e.target.value })}
          placeholder="Amount paid ₹"
          className="h-10 px-3 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss"
          data-testid="vault-amount"
        />
        <input
          type="number"
          value={form.quantity}
          onChange={(e) => setForm({ ...form, quantity: e.target.value })}
          placeholder={type === "land" ? "Acres" : "Grams"}
          className="h-10 px-3 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss"
          data-testid="vault-quantity"
        />
        <input
          value={form.purity}
          onChange={(e) => setForm({ ...form, purity: e.target.value })}
          placeholder={type === "land" ? "Type (agri)" : "Purity"}
          className="h-10 px-3 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss"
          data-testid="vault-purity"
        />
        <input
          type="date"
          value={form.purchased_on}
          onChange={(e) => setForm({ ...form, purchased_on: e.target.value })}
          className="h-10 px-3 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss"
          data-testid="vault-date"
        />
      </div>
      <textarea
        value={form.buyback_terms}
        onChange={(e) => setForm({ ...form, buyback_terms: e.target.value })}
        placeholder={
          type === "land"
            ? "Any repurchase / partition / lease-back terms (as written)"
            : "Exact buyback policy from shop — e.g. 'buys back at ±2% of day rate, no wastage deduction on coins'"
        }
        rows={2}
        className="w-full px-3 py-2 text-sm rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss mb-2"
        data-testid="vault-buyback"
      />

      <div className="flex flex-wrap items-center gap-3 mb-2">
        <label className="inline-flex items-center gap-1.5 text-sm text-stone_brand-dark cursor-pointer hover:text-moss">
          <Upload className="w-4 h-4" />
          Attach invoice photo
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={onFile}
            className="hidden"
            data-testid="vault-file-input"
          />
        </label>
        {form.invoice_url && (
          <span className="text-[0.7rem] font-mono text-moss">
            ✓ attached
          </span>
        )}
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-sm bg-amber_brand hover:bg-amber_brand/90 text-stone_brand-dark h-10 gap-1.5 ml-auto"
          data-testid="vault-save"
        >
          <Shield className="w-4 h-4" />
          {saving ? "Saving…" : "Save to vault"}
        </Button>
      </div>

      {/* List */}
      <div className="mt-5 border-t border-line pt-4">
        <div className="text-[0.65rem] font-mono uppercase tracking-widest text-stone_brand-light mb-2">
          Your {type} purchases
        </div>
        {loading ? (
          <div className="text-xs text-stone_brand-light">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-stone_brand-light">
            No entries yet — save your first purchase above.
          </div>
        ) : (
          <div className="space-y-2" data-testid="vault-list">
            {items.map((it) => (
              <div
                key={it.id}
                className="bg-bone-50 border border-line rounded-sm p-3 flex items-start justify-between gap-3"
                data-testid={`vault-item-${it.id}`}
              >
                <div className="flex-1 text-sm">
                  <div className="font-medium text-stone_brand-dark">
                    {it.seller || "—"}
                    {it.city ? (
                      <span className="text-stone_brand-light font-normal"> · {it.city}</span>
                    ) : null}
                  </div>
                  <div className="text-xs text-stone_brand mt-0.5">
                    {it.quantity ? `${it.quantity} ${it.unit || ""}` : ""}
                    {it.purity ? ` · ${it.purity}` : ""}
                    {it.amount_paid
                      ? ` · ₹${Number(it.amount_paid).toLocaleString("en-IN")}`
                      : ""}
                    {it.purchased_on ? ` · ${it.purchased_on}` : ""}
                  </div>
                  {it.buyback_terms && (
                    <div className="text-[0.7rem] text-amber_brand mt-1 italic">
                      buyback: {it.buyback_terms}
                    </div>
                  )}
                  {it.invoice_url && (
                    <a
                      href={it.invoice_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[0.7rem] text-moss inline-flex items-center gap-1 mt-1 hover:underline"
                    >
                      <FileText className="w-3 h-3" /> View invoice
                    </a>
                  )}
                </div>
                <button
                  onClick={() => remove(it.id)}
                  className="p-1.5 text-stone_brand-light hover:text-terracotta"
                  data-testid={`vault-delete-${it.id}`}
                  title="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
